import "./ApprovalMatrix.css";

import { useState, useEffect, useCallback } from "react";

import axios from "axios";

import editIcon from "../assets/icons/edit.png";

import deleteIcon from "../assets/icons/delete.png";

import BackIcon from "../assets/icons/arrow.png";

import { useAuthStore } from "../Services/authStore";

import { toastSuccess, toastError, toastWarning } from "../Services/toaster";

const EMPTY_APPROVAL_ROW = {
  id: 0,
  approval_level: "",
  approver_user_id: "",
};
function ApprovalMatrix() {
  const [showForm, setShowForm] = useState(false);

  const [isEditMode, setIsEditMode] = useState(false);

  const [selectedDocId, setSelectedDocId] = useState(null);

  const [approvals] = useState([]);

  const [loading, setLoading] = useState(false);

  const [documents, setDocuments] = useState([]);

  const [users, setUsers] = useState([]);

  const [documentId, setDocumentId] = useState("");

  const [approvalRows, setApprovalRows] = useState([{ ...EMPTY_APPROVAL_ROW }]);

  const [formError, setFormError] = useState("");

  const [formLoading, setFormLoading] = useState(false);

  const token = useAuthStore((state) => state.token);

  const authHeaders = useCallback(
    () => ({
      Authorization: `Bearer ${token}`,

      accept: "application/json",
    }),

    [token],
  );

  // ── GET /document/list → only isactive === 1 ───────────

  const getDocuments = useCallback(async () => {
    try {
      setLoading(true);

      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/document/list`,
        { headers: authHeaders() },
      );

      const all = res.data.data || res.data;
      setDocuments(all.filter((d) => d.isactive === 1));
    } catch (err) {
      console.error("Failed to load documents:", err.response?.data);
      toastError("Failed to load documents.");
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  const getUsers = useCallback(async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/users/list`,

        { headers: authHeaders() },
      );

      setUsers(res.data.data || res.data);
    } catch (err) {
      console.error("Failed to load users:", err.response?.data);

      toastError("Failed to load users.");
    }
  }, [authHeaders]);

  useEffect(() => {
    getDocuments();
  }, [getDocuments]);

  // ── Open Edit ───────────────────────────────────────────

  const openEdit = async (docId) => {
    try {
      setFormLoading(true);
      setFormError("");

      await getUsers();

      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/approval/document_by_id/${docId}`,
        {
          headers: authHeaders(),
        },
      );

      const approvals = res.data.data || [];

      setDocumentId(String(docId));

      setApprovalRows(
        approvals.map((item) => ({
          id: item.id,
          approval_level: String(item.approval_level),
          approver_user_id: String(item.approver_user_id),
          is_active: item.isactive,
        })),
      );

      setSelectedDocId(docId);
      setIsEditMode(true);
      setShowForm(true);
    } catch (err) {
      console.log(err.response?.data);
      toastError("Failed to load approval matrix.");
    } finally {
      setFormLoading(false);
    }
  };

  const openAdd = async () => {
    await getUsers();

    setIsEditMode(false);

    setSelectedDocId(null);

    setDocumentId("");

    setApprovalRows([{ ...EMPTY_APPROVAL_ROW }]);

    setFormError("");

    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);

    setDocumentId("");

    setApprovalRows([{ ...EMPTY_APPROVAL_ROW }]);

    setFormError("");

    setSelectedDocId(null);

    setIsEditMode(false);
  };

  const handleRowChange = (index, field, value) => {
    const updated = approvalRows.map((row, i) =>
      i === index ? { ...row, [field]: value } : row,
    );

    setApprovalRows(updated);
  };

  const addRow = () => {
    setApprovalRows([...approvalRows, { ...EMPTY_APPROVAL_ROW }]);
  };

  const removeRow = (index) => {
    if (approvalRows.length === 1) return;

    setApprovalRows(approvalRows.filter((_, i) => i !== index));
  };

  const validate = () => {
    if (!documentId) return "Please select a document.";

    for (let i = 0; i < approvalRows.length; i++) {
      const row = approvalRows[i];

      if (!row.approval_level)
        return `Row ${i + 1}: Approval level is required.`;

      if (!row.approver_user_id) return `Row ${i + 1}: Approver is required.`;
    }

    return "";
  };

  const buildPayload = () => ({
    document_id: parseInt(documentId, 10),

    approvals: approvalRows.map((row) => ({
      id: parseInt(row.id, 10) || 0,

      approval_level: parseInt(row.approval_level, 10),

      approver_user_id: parseInt(row.approver_user_id, 10),

      is_active: row.is_active !== undefined ? parseInt(row.is_active, 10) : 1,
    })),
  });

  const handleApiError = (err, fallback) => {
    const detail = err.response?.data?.detail;

    const msg = Array.isArray(detail)
      ? detail.map((d) => `${d.loc?.join(".")} — ${d.msg}`).join("\n")
      : detail || fallback;

    setFormError(msg);

    toastError(msg);
  };

  // ── POST /approval/add ──────────────────────────────────

  const handleSubmit = async () => {
    setFormError("");

    const err = validate();

    if (err) {
      setFormError(err);

      toastWarning(err);

      return;
    }

    setFormLoading(true);

    try {
      const authToken = useAuthStore.getState().token;

      await axios.post(
        `${process.env.REACT_APP_API_URL}/approval/add`,

        buildPayload(),

        {
          headers: {
            Authorization: `Bearer ${authToken}`,

            accept: "application/json",

            "Content-Type": "application/json",
          },
        },
      );

      toastSuccess("Approval matrix created successfully.");

      closeForm();

      getDocuments();
    } catch (err) {
      handleApiError(err, "Failed to create approval matrix.");
    } finally {
      setFormLoading(false);
    }
  };

  // ── PUT /approval/update/{document_id} ─────────────────

  const handleUpdate = async () => {
    setFormError("");

    const err = validate();

    if (err) {
      setFormError(err);

      toastWarning(err);

      return;
    }

    setFormLoading(true);

    try {
      const authToken = useAuthStore.getState().token;

      await axios.put(
        `${process.env.REACT_APP_API_URL}/approval/update/${selectedDocId}`,
        buildPayload(),
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
            "Content-Type": "application/json",
            accept: "application/json",
          },
        },
      );
      toastSuccess("Approval matrix updated successfully.");

      closeForm();

      getDocuments();
    } catch (err) {
      handleApiError(err, "Failed to update approval matrix.");
    } finally {
      setFormLoading(false);
    }
  };

  // ── DELETE /approval/delete/{document_id} ──────────────

  const handleDelete = async (docId, docName) => {
    if (
      !window.confirm(
        `Delete approval matrix for "${docName}"? This cannot be undone.`,
      )
    )
      return;

    try {
      const authToken = useAuthStore.getState().token;

      await axios.delete(
        `${process.env.REACT_APP_API_URL}/approval/delete/${docId}`,

        {
          headers: {
            Authorization: `Bearer ${authToken}`,

            accept: "application/json",
          },
        },
      );

      toastSuccess("Approval matrix deleted successfully.");

      getDocuments();
    } catch (err) {
      console.error("Delete error:", err.response?.data);

      toastError(
        err.response?.data?.detail || "Failed to delete approval matrix.",
      );
    }
  };

  // ════════════════════════════════════════════════════════

  //  ADD / EDIT FORM

  // ════════════════════════════════════════════════════════

  if (showForm) {
    return (
      <div className="approval-matrix-form-page">
        <div className="form-page-header">
          <div className="form-back-btn-wrapper">
            <button className="form-back-btn" onClick={closeForm}>
              <img src={BackIcon} alt="Back" className="form-back-icon" />
            </button>
          </div>

          <div className="form-header-title">
            <h2>
              {isEditMode ? "Edit Approval Matrix" : "Add Approval Matrix"}
            </h2>
          </div>
        </div>

        <div className="form-card">
          {formLoading && <p className="form-info-text">Loading...</p>}

          {formError && <p className="form-error-text">{formError}</p>}

          <div className="form-group form-doc-group">
            <label>Document *</label>

            <select
              className="form-select"
              value={documentId}
              onChange={(e) => setDocumentId(e.target.value)}
            >
              <option value="">Select Document</option>

              {documents.map((doc) => (
                <option key={doc.id} value={doc.id}>
                  {doc.document_name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-section-title">Approval Levels</div>

          <div className="form-table-wrap">
            <table className="form-table">
              <thead>
                <tr>
                  <th>#</th>

                  <th>Approval Level *</th>

                  <th>Approver *</th>

                  <th>Status</th>

                  <th>Action</th>
                </tr>
              </thead>

              <tbody>
                {approvalRows.map((row, index) => (
                  <tr key={index}>
                    <td className="form-row-num">{index + 1}</td>

                    <td>
                      <input
                        className="form-input"
                        type="number"
                        min="1"
                        placeholder="e.g. 1"
                        value={row.approval_level}
                        onChange={(e) =>
                          handleRowChange(
                            index,

                            "approval_level",

                            e.target.value,
                          )
                        }
                      />
                    </td>

                    <td>
                      <select
                        className="form-select"
                        value={row.approver_user_id}
                        onChange={(e) =>
                          handleRowChange(
                            index,

                            "approver_user_id",

                            e.target.value,
                          )
                        }
                      >
                        <option value="">Select Approver</option>

                        {users.map((u) => (
                          <option key={u.id} value={u.id}>
                            {u.first_name} {u.last_name}
                          </option>
                        ))}
                      </select>
                    </td>

                    <td>
                      <select
                        className="form-select"
                        value={row.is_active}
                        onChange={(e) =>
                          handleRowChange(
                            index,

                            "is_active",

                            parseInt(e.target.value, 10),
                          )
                        }
                      >
                        <option value={1}>Active</option>

                        <option value={0}>Inactive</option>
                      </select>
                    </td>

                    <td>
                      <button
                        className="delete-btn"
                        onClick={() => removeRow(index)}
                        title="Remove row"
                        disabled={approvalRows.length === 1}
                      >
                        <img src={deleteIcon} alt="Delete" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <button className="form-add-row-btn" onClick={addRow}>
            <i className="ti ti-plus"></i> Add Level
          </button>

          <div className="form-footer-actions">
            <button className="btn-cancel" onClick={closeForm}>
              Cancel
            </button>

            <button
              className="btn-save"
              onClick={isEditMode ? handleUpdate : handleSubmit}
              disabled={formLoading}
            >
              {formLoading
                ? "Saving..."
                : isEditMode
                  ? "Update Matrix"
                  : "Save Matrix"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="approval-list-page">
      <div className="list-page-header">
        <div className="list-page-title">
          <h2>Approval Matrix</h2>
          <p>Configure approval levels per document type</p>
        </div>

        <div className="list-page-actions">
          <button className="btn-add-matrix" onClick={openAdd}>
            <i className="ti ti-plus"></i>
            <span>Add Approval Matrix</span>
          </button>
        </div>
      </div>

      <div className="list-table-card">
        {loading ? (
          <p style={{ padding: "20px", color: "#9F8E87", fontSize: 13 }}>
            Loading approval matrix...
          </p>
        ) : documents.length === 0 ? (
          <p style={{ padding: "20px", color: "#9F8E87", fontSize: 13 }}>
            No documents found.
          </p>
        ) : (
          <table className="approval-table">
            <colgroup>
              <col style={{ width: "200px" }} />
              <col />
              <col style={{ width: "390px" }} />
            </colgroup>

            <thead>
              <tr>
                <th>#</th>
                <th>Document Name</th>
                <th>Action</th>
              </tr>
            </thead>

            <tbody>
              {documents.map((doc, index) => {
                const hasApprovals = approvals.some(
                  (a) => a.document_id === doc.id || a.id === doc.id,
                );

                return (
                  <tr key={doc.id}>
                    <td className="list-cell-center">{index + 1}</td>

                    <td>{doc.document_name}</td>

                    <td className="list-action-cell">
                      <button
                        className="list-edit-btn"
                        onClick={() => openEdit(doc.id)}
                        title={hasApprovals ? "Edit" : "Add Approval"}
                      >
                        <img src={editIcon} alt="Edit" />
                      </button>

                      {hasApprovals && (
                        <button
                          className="list-delete-button"
                          onClick={() =>
                            handleDelete(doc.id, doc.document_name)
                          }
                          title="Delete"
                        >
                          <img src={deleteIcon} alt="Delete" />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
export default ApprovalMatrix;
